/**
 * Chess Square Base Component
 * Pure logic - color calculation and move rendering, no styling
 * CRITICAL: Uses correct formula (fileIndex + rank) % 2 === 0 for light squares
 */
import type { SquareProps } from "../../types";
import { isLightSquare } from "../../utils";

export interface SquareBaseProps extends SquareProps {
  /** Function to render the piece (allows different styling variants) */
  renderPiece: (piece: SquareProps["piece"]) => React.ReactNode;
  /** CSS class name for the square container */
  className?: string;
  /** Inline styles for the square container */
  style?: React.CSSProperties;
  /** CSS class name for the legal move indicator */
  legalMoveClassName?: string;
  /** Inline styles for the legal move indicator */
  legalMoveStyle?: React.CSSProperties;
}

export function SquareBase({
  file,
  rank,
  piece,
  isSelected,
  isLegalMove,
  isCapture,
  isAugmented,
  onClick,
  isMyTurn,
  gameEnded,
  renderPiece,
  className,
  style,
  legalMoveClassName,
  legalMoveStyle,
}: SquareBaseProps) {
  const isLight = isLightSquare(file, rank);
  const isClickable = !gameEnded && (isMyTurn === undefined || isMyTurn);

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={className}
      style={style}
      data-square={`${file}${rank}`}
      data-light={isLight}
      data-selected={isSelected}
      data-legal-move={isLegalMove}
      data-capture={isCapture}
      data-augmented={isAugmented || undefined}
    >
      {piece && renderPiece(piece)}
      {isLegalMove && !piece && (
        <div className={legalMoveClassName} style={legalMoveStyle} />
      )}
    </div>
  );
}
